import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Search, Calendar, Users, AlertCircle, DollarSign, ArrowUpDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { filterJobsByPermissions } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPS_STAGES = ['scheduled', 'prep', 'production', 'installation', 'final_inspection'];

export const JobQueue = () => {
  const { user, viewAsRole, viewAsUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('install_date');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyRoleBasedFilter();
  }, [allJobs, user, viewAsRole, viewAsUser, users]);

  const fetchData = async () => {
    try {
      const [jobsRes, usersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`)
      ]);
      
      setAllJobs(jobsRes.data);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load job queue');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilter = () => {
    if (!allJobs || allJobs.length === 0) {
      setJobs([]);
      return;
    }

    let effectiveUser = user;
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) effectiveUser = testUser;
    }

    const filteredJobs = filterJobsByPermissions(allJobs, effectiveUser, users);
    const opsJobs = filteredJobs.filter(job => OPS_STAGES.includes(job.stage));
    setJobs(opsJobs);
  };

  const handleAssignCrew = async (jobId, teamId) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}`, {
        assigned_team: teamId
      });
      toast.success('Crew assigned successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to assign crew:', error);
      toast.error('Failed to assign crew');
    }
  };

  const filteredAndSortedJobs = () => {
    let filtered = jobs.filter(job => {
      const matchesSearch = job.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           job.address?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStage = filterStage === 'all' || job.stage === filterStage;
      const matchesTeam = filterTeam === 'all' || job.assigned_team === filterTeam;
      const matchesPriority = filterPriority === 'all' || job.priority === filterPriority;
      
      return matchesSearch && matchesStage && matchesTeam && matchesPriority;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'install_date':
          aVal = a.install_date ? new Date(a.install_date) : new Date('2099-12-31');
          bVal = b.install_date ? new Date(b.install_date) : new Date('2099-12-31');
          break;
        case 'value':
          aVal = a.estimated_value || 0;
          bVal = b.estimated_value || 0;
          break;
        case 'priority':
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          aVal = priorityOrder[a.priority] || 2;
          bVal = priorityOrder[b.priority] || 2;
          break;
        default:
          aVal = a.customer_name || '';
          bVal = b.customer_name || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getTeamName = (teamId) => {
    if (!teamId) return 'Unassigned';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStageLabel = (stage) => {
    const labels = {
      scheduled: 'Scheduled',
      prep: 'Prep',
      production: 'Production',
      installation: 'Installation',
      final_inspection: 'Final Inspection'
    };
    return labels[stage] || stage;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const queueJobs = filteredAndSortedJobs();

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Job Queue</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage scheduling and crew assignments</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Stage Filter */}
              <Select value={filterStage} onValueChange={setFilterStage}>
                <SelectTrigger>
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {OPS_STAGES.map(stage => (
                    <SelectItem key={stage} value={stage}>{getStageLabel(stage)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Team Filter */}
              <Select value={filterTeam} onValueChange={setFilterTeam}>
                <SelectTrigger>
                  <SelectValue placeholder="All Crews" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Crews</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="install_date">Install Date</SelectItem>
                  <SelectItem value="value">Value</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing {queueJobs.length} job{queueJobs.length !== 1 ? 's' : ''}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                <ArrowUpDown className="h-4 w-4 mr-2" />
                {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Job Queue Table */}
        <Card>
          <CardHeader>
            <CardTitle>Active Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-semibold">Priority</th>
                    <th className="text-left p-3 text-sm font-semibold">Customer</th>
                    <th className="text-left p-3 text-sm font-semibold">Stage</th>
                    <th className="text-left p-3 text-sm font-semibold">Install Date</th>
                    <th className="text-left p-3 text-sm font-semibold">Value</th>
                    <th className="text-left p-3 text-sm font-semibold">Crew</th>
                    <th className="text-left p-3 text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {queueJobs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center p-8 text-gray-500">
                        No jobs in queue
                      </td>
                    </tr>
                  ) : (
                    queueJobs.map(job => (
                      <tr key={job.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="p-3">
                          <Badge className={getPriorityColor(job.priority)}>
                            {job.priority || 'medium'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{job.customer_name}</p>
                            <p className="text-xs text-gray-500">{job.job_type}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{getStageLabel(job.stage)}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {job.install_date ? new Date(job.install_date).toLocaleDateString() : 'Not set'}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-sm font-semibold text-green-700">
                            <DollarSign className="h-4 w-4" />
                            {formatCurrency(job.estimated_value)}
                          </div>
                        </td>
                        <td className="p-3">
                          <Select
                            value={job.assigned_team || 'unassigned'}
                            onValueChange={(value) => handleAssignCrew(job.id, value === 'unassigned' ? null : value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Assign crew" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {teams.map(team => (
                                <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <Button variant="ghost" size="sm">View Details</Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};